/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

!function (i) { "use strict"; "function" == typeof define && define.amd ? define(["jquery"], i) : "undefined" != typeof exports ? module.exports = i(require("jquery")) : i(jQuery) }(function (i) { "use strict"; var e = window.Slick || {}; (e = function () { var e = 0; return function (t, o) { var s, n = this; n.defaults = { accessibility: !0, adaptiveHeight: !1, appendArrows: i(t), appendDots: i(t), arrows: !0, asNavFor: null, prevArrow: '<button class="slick-prev" aria-label="Previous" type="button">Previous</button>', nextArrow: '<button class="slick-next" aria-label="Next" type="button">Next</button>', autoplay: !1, autoplaySpeed: 3e3, centerMode: !1, centerPadding: "50px", cssEase: "ease", customPaging: function (e, t) { return i('<button type="button" />').text(t + 1) }, dots: !1, dotsClass: "slick-dots", draggable: !0, easing: "linear", edgeFriction: .35, fade: !1, focusOnSelect: !1, focusOnChange: !1, infinite: !0, initialSlide: 0, lazyLoad: "ondemand", mobileFirst: !1, pauseOnHover: !0, pauseOnFocus: !0, pauseOnDotsHover: !1, respondTo: "window", responsive: null, rows: 1, rtl: !1, slide: "", slidesPerRow: 1, slidesToShow: 1, slidesToScroll: 1, speed: 500, swipe: !0, swipeToSlide: !1, touchMove: !0, touchThreshold: 5, useCSS: !0, useTransform: !0, variableWidth: !1, vertical: !1, verticalSwiping: !1, waitForAnimate: !0, zIndex: 1e3 }, n.initials = { animating: !1, dragging: !1, autoPlayTimer: null, currentDirection: 0, currentLeft: null, currentSlide: 0, direction: 1, $dots: null, listWidth: null, listHeight: null, loadIndex: 0, $nextArrow: null, $prevArrow: null, scrolling: !1, slideCount: null, slideWidth: null, $slideTrack: null, $slides: null, sliding: !1, slideOffset: 0, swipeLeft: null, swiping: !1, $list: null, touchObject: {}, transformsEnabled: !1, unslicked: !1 }, i.extend(n, n.initials), n.activeBreakpoint = null, n.animType = null, n.animProp = null, n.breakpoints = [], n.breakpointSettings = [], n.cssTransitions = !1, n.focussed = !1, n.interrupted = !1, n.hidden = "hidden", n.paused = !0, n.positionProp = null, n.respondTo = null, n.rowCount = 1, n.shouldClick = !0, n.$slider = i(t), n.$slidesCache = null, n.transformType = null, n.transitionType = null, n.visibilityChange = "visibilitychange", n.windowWidth = 0, n.windowTimer = null, s = i(t).data("slick") || {}, n.options = i.extend({}, n.defaults, o, s), n.currentSlide = n.options.initialSlide, n.originalSettings = n.options, void 0 !== document.mozHidden ? (n.hidden = "mozHidden", n.visibilityChange = "mozvisibilitychange") : void 0 !== document.webkitHidden && (n.hidden = "webkitHidden", n.visibilityChange = "webkitvisibilitychange"), n.autoPlay = i.proxy(n.autoPlay, n), n.autoPlayClear = i.proxy(n.autoPlayClear, n), n.autoPlayIterator = i.proxy(n.autoPlayIterator, n), n.changeSlide = i.proxy(n.changeSlide, n), n.clickHandler = i.proxy(n.clickHandler, n), n.selectHandler = i.proxy(n.selectHandler, n), n.setPosition = i.proxy(n.setPosition, n), n.swipeHandler = i.proxy(n.swipeHandler, n), n.dragHandler = i.proxy(n.dragHandler, n), n.keyHandler = i.proxy(n.keyHandler, n), n.instanceUid = e++, n.htmlExpr = /^(?:\s*(<[\w\W]+>)[^>]*)$/, n.registerBreakpoints(), n.init(!0) } }()).prototype.activateADA = function () { this.$slideTrack.find(".slick-active").attr({ "aria-hidden": "false" }).find("a, input, button, select").attr({ tabindex: "0" }) }, e.prototype.addSlide = e.prototype.slickAdd = function (e, t, o) { var s = this; if ("boolean" == typeof t) o = t, t = null; else if (t < 0 || t >= s.slideCount) return !1; s.unload(), "number" == typeof t ? 0 === t && 0 === s.$slides.length ? i(e).appendTo(s.$slideTrack) : o ? i(e).insertBefore(s.$slides.eq(t)) : i(e).insertAfter(s.$slides.eq(t)) : !0 === o ? i(e).prependTo(s.$slideTrack) : i(e).appendTo(s.$slideTrack), s.$slides = s.$slideTrack.children(this.options.slide), s.$slideTrack.children(this.options.slide).detach(), s.$slideTrack.append(s.$slides), s.$slides.each(function (e, t) { i(t).attr("data-slick-index", e) }), s.$slidesCache = s.$slides, s.reinit() }, e.prototype.animateHeight = function () { var i = this; if (1 === i.options.slidesToShow && !0 === i.options.adaptiveHeight && !1 === i.options.vertical) { var e = i.$slides.eq(i.currentSlide).outerHeight(!0); i.$list.animate({ height: e }, i.options.speed) } }, e.prototype.animateSlide = function (e, t) { var o = {}, s = this; s.animateHeight(), !0 === s.options.rtl && !1 === s.options.vertical && (e = -e), !1 === s.transformsEnabled ? !1 === s.options.vertical ? s.$slideTrack.animate({ left: e }, s.options.speed, s.options.easing, t) : s.$slideTrack.animate({ top: e }, s.options.speed, s.options.easing, t) : !1 === s.cssTransitions ? (!0 === s.options.rtl && (s.currentLeft = -s.currentLeft), i({ animStart: s.currentLeft }).animate({ animStart: e }, { duration: s.options.speed, easing: s.options.easing, step: function (i) { i = Math.ceil(i), !1 === s.options.vertical ? (o[s.animType] = "translate(" + i + "px, 0px)", s.$slideTrack.css(o)) : (o[s.animType] = "translate(0px," + i + "px)", s.$slideTrack.css(o)) }, complete: function () { t && t.call() } })) : (s.applyTransition(), e = Math.ceil(e), !1 === s.options.vertical ? o[s.animType] = "translate3d(" + e + "px, 0px, 0px)" : o[s.animType] = "translate3d(0px," + e + "px, 0px)", s.$slideTrack.css(o), t && setTimeout(function () { s.disableTransition(), t.call() }, s.options.speed)) }, e.prototype.getNavTarget = function () { var e = this, t = e.options.asNavFor; return t && null !== t && (t = i(t).not(e.$slider)), t }, e.prototype.asNavFor = function (e) { var t = this.getNavTarget(); null !== t && "object" == typeof t && t.each(function () { var t = i(this).slick("getSlick"); t.unslicked || t.slideHandler(e, !0) }) }, e.prototype.applyTransition = function (i) { var e = this, t = {}; !1 === e.options.fade ? t[e.transitionType] = e.transformType + " " + e.options.speed + "ms " + e.options.cssEase : t[e.transitionType] = "opacity " + e.options.speed + "ms " + e.options.cssEase, !1 === e.options.fade ? e.$slideTrack.css(t) : e.$slides.eq(i).css(t) }, e.prototype.autoPlay = function () { var i = this; i.autoPlayClear(), i.slideCount > i.options.slidesToShow && (i.autoPlayTimer = setInterval(i.autoPlayIterator, i.options.autoplaySpeed)) }, e.prototype.autoPlayClear = function () { var i = this; i.autoPlayTimer && clearInterval(i.autoPlayTimer) }, e.prototype.autoPlayIterator = function () { var i = this, e = i.currentSlide + i.options.slidesToScroll; i.paused || i.interrupted || i.focussed || (!1 === i.options.infinite && (1 === i.direction && i.currentSlide + 1 === i.slideCount - 1 ? i.direction = 0 : 0 === i.direction && (e = i.currentSlide - i.options.slidesToScroll, i.currentSlide - 1 == 0 && (i.direction = 1))), i.slideHandler(e)) }, e.prototype.buildArrows = function () { var e = this; !0 === e.options.arrows && (e.$prevArrow = i(e.options.prevArrow).addClass("slick-arrow"), e.$nextArrow = i(e.options.nextArrow).addClass("slick-arrow"), e.slideCount > e.options.slidesToShow ? (e.$prevArrow.removeClass("slick-hidden").removeAttr("aria-hidden tabindex"), e.$nextArrow.removeClass("slick-hidden").removeAttr("aria-hidden tabindex"), e.htmlExpr.test(e.options.prevArrow) && e.$prevArrow.prependTo(e.options.appendArrows), e.htmlExpr.test(e.options.nextArrow) && e.$nextArrow.appendTo(e.options.appendArrows), !0 !== e.options.infinite && e.$prevArrow.addClass("slick-disabled").attr("aria-disabled", "true")) : e.$prevArrow.add(e.$nextArrow).addClass("slick-hidden").attr({ "aria-disabled": "true", tabindex: "-1" })) }, e.prototype.buildDots = function () { var e, t, o = this; if (!0 === o.options.dots) { for (o.$slider.addClass("slick-dotted"), t = i("<ul />").addClass(o.options.dotsClass), e = 0; e <= o.getDotCount(); e += 1)t.append(i("<li />").append(o.options.customPaging.call(this, o, e))); o.$dots = t.appendTo(o.options.appendDots), o.$dots.find("li").first().addClass("slick-active") } }, e.prototype.buildOut = function () { var e = this; e.$slides = e.$slider.children(e.options.slide + ":not(.slick-cloned)").addClass("slick-slide"), e.slideCount = e.$slides.length, e.$slides.each(function (e, t) { i(t).attr("data-slick-index", e).data("originalStyling", i(t).attr("style") || "") }), e.$slider.addClass("slick-slider"), e.$slideTrack = 0 === e.slideCount ? i('<div class="slick-track"/>').appendTo(e.$slider) : e.$slides.wrapAll('<div class="slick-track"/>').parent(), e.$list = e.$slideTrack.wrap('<div class="slick-list"/>').parent(), e.$slideTrack.css("opacity", 0), !0 !== e.options.centerMode && !0 !== e.options.swipeToSlide || (e.options.slidesToScroll = 1), i("img[data-lazy]", e.$slider).not("[src]").addClass("slick-loading"), e.setupInfinite(), e.buildArrows(), e.buildDots(), e.updateDots(), e.setSlideClasses("number" == typeof e.currentSlide ? e.currentSlide : 0), !0 === e.options.draggable && e.$list.addClass("draggable") }, e.prototype.buildRows = function () { var i, e, t, o, s, n, r, l = this; if (o = document.createDocumentFragment(), n = l.$slider.children(), l.options.rows > 1) { for (r = l.options.slidesPerRow * l.options.rows, s = Math.ceil(n.length / r), i = 0; i < s; i++) { var d = document.createElement("div"); for (e = 0; e < l.options.rows; e++) { var a = document.createElement("div"); for (t = 0; t < l.options.slidesPerRow; t++) { var c = i * r + (e * l.options.slidesPerRow + t); n.get(c) && a.appendChild(n.get(c)) } d.appendChild(a) } o.appendChild(d) } l.$slider.empty().append(o), l.$slider.children().children().children().css({ width: 100 / l.options.slidesPerRow + "%", display: "inline-block" }) } }, e.prototype.checkResponsive = function (e, t) { var o, s, n, r = this, l = !1, d = r.$slider.width(), a = window.innerWidth || i(window).width(); if ("window" === r.respondTo ? n = a : "slider" === r.respondTo ? n = d : "min" === r.respondTo && (n = Math.min(a, d)), r.options.responsive && r.options.responsive.length && null !== r.options.responsive) { s = null; for (o in r.breakpoints) r.breakpoints.hasOwnProperty(o) && (!1 === r.originalSettings.mobileFirst ? n < r.breakpoints[o] && (s = r.breakpoints[o]) : n > r.breakpoints[o] && (s = r.breakpoints[o])); null !== s ? null !== r.activeBreakpoint ? (s !== r.activeBreakpoint || t) && (r.activeBreakpoint = s, "unslick" === r.breakpointSettings[s] ? r.unslick(s) : (r.options = i.extend({}, r.originalSettings, r.breakpointSettings[s]), !0 === e && (r.currentSlide = r.options.initialSlide), r.refresh(e)), l = s) : (r.activeBreakpoint = s, "unslick" === r.breakpointSettings[s] ? r.unslick(s) : (r.options = i.extend({}, r.originalSettings, r.breakpointSettings[s]), !0 === e && (r.currentSlide = r.options.initialSlide), r.refresh(e)), l = s) : null !== r.activeBreakpoint && (r.activeBreakpoint = null, r.options = r.originalSettings, !0 === e && (r.currentSlide = r.options.initialSlide), r.refresh(e), l = s), e || !1 === l || r.$slider.trigger("breakpoint", [r, l]) } }, e.prototype.changeSlide = function (e, t) { var o, s, n, r = this, l = i(e.currentTarget); switch (l.is("a") && e.preventDefault(), l.is("li") || (l = l.closest("li")), n = r.slideCount % r.options.slidesToScroll != 0, o = n ? 0 : (r.slideCount - r.currentSlide) % r.options.slidesToScroll, e.data.message) { case "previous": s = 0 === o ? r.options.slidesToScroll : r.options.slidesToShow - o, r.slideCount > r.options.slidesToShow && r.slideHandler(r.currentSlide - s, !1, t); break; case "next": s = 0 === o ? r.options.slidesToScroll : o, r.slideCount > r.options.slidesToShow && r.slideHandler(r.currentSlide + s, !1, t); break; case "index": var d = 0 === e.data.index ? 0 : e.data.index || l.index() * r.options.slidesToScroll; r.slideHandler(r.checkNavigable(d), !1, t), l.children().trigger("focus"); break; default: return } }, e.prototype.checkNavigable = function (i) { var e, t; if (e = this.getNavigableIndexes(), t = 0, i > e[e.length - 1]) i = e[e.length - 1]; else for (var o in e) { if (i < e[o]) { i = t; break } t = e[o] } return i }, e.prototype.cleanUpEvents = function () { var e = this; e.options.dots && null !== e.$dots && (i("li", e.$dots).off("click.slick", e.changeSlide).off("mouseenter.slick", i.proxy(e.interrupt, e, !0)).off("mouseleave.slick", i.proxy(e.interrupt, e, !1)), !0 === e.options.accessibility && e.$dots.off("keydown.slick", e.keyHandler)), e.$slider.off("focus.slick blur.slick"), !0 === e.options.arrows && e.slideCount > e.options.slidesToShow && (e.$prevArrow && e.$prevArrow.off("click.slick", e.changeSlide), e.$nextArrow && e.$nextArrow.off("click.slick", e.changeSlide), !0 === e.options.accessibility && (e.$prevArrow && e.$prevArrow.off("keydown.slick", e.keyHandler), e.$nextArrow && e.$nextArrow.off("keydown.slick", e.keyHandler))), e.$list.off("touchstart.slick mousedown.slick", e.swipeHandler), e.$list.off("touchmove.slick mousemove.slick", e.swipeHandler), e.$list.off("touchend.slick mouseup.slick", e.swipeHandler), e.$list.off("touchcancel.slick mouseleave.slick", e.swipeHandler), e.$list.off("click.slick", e.clickHandler), i(document).off(e.visibilityChange, e.visibility), e.cleanUpSlideEvents(), !0 === e.options.accessibility && e.$list.off("keydown.slick", e.keyHandler), !0 === e.options.focusOnSelect && i(e.$slideTrack).children().off("click.slick", e.selectHandler), i(window).off("orientationchange.slick.slick-" + e.instanceUid, e.orientationChange), i(window).off("resize.slick.slick-" + e.instanceUid, e.resize), i("[draggable!=true]", e.$slideTrack).off("dragstart", e.preventDefault), i(window).off("load.slick.slick-" + e.instanceUid, e.setPosition) }, e.prototype.cleanUpSlideEvents = function () { var e = this; e.$list.off("mouseenter.slick", i.proxy(e.interrupt, e, !0)), e.$list.off("mouseleave.slick", i.proxy(e.interrupt, e, !1)) }, e.prototype.cleanUpRows = function () { var i, e = this; e.options.rows > 1 && ((i = e.$slides.children().children()).removeAttr("style"), e.$slider.empty().append(i)) }, e.prototype.clickHandler = function (i) { !1 === this.shouldClick && (i.stopImmediatePropagation(), i.stopPropagation(), i.preventDefault()) }, e.prototype.destroy = function (e) { var t = this; t.autoPlayClear(), t.touchObject = {}, t.cleanUpEvents(), i(".slick-cloned", t.$slider).detach(), t.$dots && t.$dots.remove(), t.$prevArrow && t.$prevArrow.length && (t.$prevArrow.removeClass("slick-disabled slick-arrow slick-hidden").removeAttr("aria-hidden aria-disabled tabindex").css("display", ""), t.htmlExpr.test(t.options.prevArrow) && t.$prevArrow.remove()), t.$nextArrow && t.$nextArrow.length && (t.$nextArrow.removeClass("slick-disabled slick-arrow slick-hidden").removeAttr("aria-hidden aria-disabled tabindex").css("display", ""), t.htmlExpr.test(t.options.nextArrow) && t.$nextArrow.remove()), t.$slides && (t.$slides.removeClass("slick-slide slick-active slick-center slick-visible slick-current").removeAttr("aria-hidden").removeAttr("data-slick-index").each(function () { i(this).attr("style", i(this).data("originalStyling")) }), t.$slideTrack.children(this.options.slide).detach(), t.$slideTrack.detach(), t.$list.detach(), t.$slider.append(t.$slides)), t.cleanUpRows(), t.$slider.removeClass("slick-slider"), t.$slider.removeClass("slick-initialized"), t.$slider.removeClass("slick-dotted"), t.unslicked = !0, e || t.$slider.trigger("destroy", [t]) }, e.prototype.disableTransition = function (i) { var e = this, t = {}; t[e.transitionType] = "", !1 === e.options.fade ? e.$slideTrack.css(t) : e.$slides.eq(i).css(t) }, e.prototype.fadeSlide = function (i, e) { var t = this; !1 === t.cssTransitions ? (t.$slides.eq(i).css({ zIndex: t.options.zIndex }), t.$slides.eq(i).animate({ opacity: 1 }, t.options.speed, t.options.easing, e)) : (t.applyTransition(i), t.$slides.eq(i).css({ opacity: 1, zIndex: t.options.zIndex }), e && setTimeout(function () { t.disableTransition(i), e.call() }, t.options.speed)) }, e.prototype.fadeSlideOut = function (i) { var e = this; !1 === e.cssTransitions ? e.$slides.eq(i).animate({ opacity: 0, zIndex: e.options.zIndex - 2 }, e.options.speed, e.options.easing) : (e.applyTransition(i), e.$slides.eq(i).css({ opacity: 0, zIndex: e.options.zIndex - 2 })) }, e.prototype.filterSlides = e.prototype.slickFilter = function (i) { var e = this; null !== i && (e.$slidesCache = e.$slides, e.unload(), e.$slideTrack.children(this.options.slide).detach(), e.$slidesCache.filter(i).appendTo(e.$slideTrack), e.reinit()) }, e.prototype.focusHandler = function () { var e = this; e.$slider.off("focus.slick blur.slick").on("focus.slick blur.slick", "*", function (t) { t.stopImmediatePropagation(); var o = i(this); setTimeout(function () { e.options.pauseOnFocus && (e.focussed = o.is(":focus"), e.autoPlay()) }, 0) }) }, e.prototype.getCurrent = e.prototype.slickCurrentSlide = function () { return this.currentSlide }, e.prototype.getDotCount = function () { var i = this, e = 0, t = 0, o = 0; if (!0 === i.options.infinite) if (i.slideCount <= i.options.slidesToShow) ++o; else for (; e < i.slideCount;)++o, e = t + i.options.slidesToScroll, t += i.options.slidesToScroll <= i.options.slidesToShow ? i.options.slidesToScroll : i.options.slidesToShow; else if (!0 === i.options.centerMode) o = i.slideCount; else if (i.options.asNavFor) for (; e < i.slideCount;)++o, e = t + i.options.slidesToScroll, t += i.options.slidesToScroll <= i.options.slidesToShow ? i.options.slidesToScroll : i.options.slidesToShow; else o = 1 + Math.ceil((i.slideCount - i.options.slidesToShow) / i.options.slidesToScroll); return o - 1 }, e.prototype.getLeft = function (i) { var e, t, o, s, n = this, r = 0; return n.slideOffset = 0, t = n.$slides.first().outerHeight(!0), !0 === n.options.infinite ? (n.slideCount > n.options.slidesToShow && (n.slideOffset = n.slideWidth * n.options.slidesToShow * -1, s = -1, !0 === n.options.vertical && !0 === n.options.centerMode && (2 === n.options.slidesToShow ? s = -1.5 : 1 === n.options.slidesToShow && (s = -2)), r = t * n.options.slidesToShow * s), n.slideCount % n.options.slidesToScroll != 0 && i + n.options.slidesToScroll > n.slideCount && n.slideCount > n.options.slidesToShow && (i > n.slideCount ? (n.slideOffset = (n.options.slidesToShow - (i - n.slideCount)) * n.slideWidth * -1, r = (n.options.slidesToShow - (i - n.slideCount)) * t * -1) : (n.slideOffset = n.slideCount % n.options.slidesToScroll * n.slideWidth * -1, r = n.slideCount % n.options.slidesToScroll * t * -1))) : i + n.options.slidesToShow > n.slideCount && (n.slideOffset = (i + n.options.slidesToShow - n.slideCount) * n.slideWidth, r = (i + n.options.slidesToShow - n.slideCount) * t), n.slideCount <= n.options.slidesToShow && (n.slideOffset = 0, r = 0), !0 === n.options.centerMode && n.slideCount <= n.options.slidesToShow ? n.slideOffset = n.slideWidth * Math.floor(n.options.slidesToShow) / 2 - n.slideWidth * n.slideCount / 2 : !0 === n.options.centerMode && !0 === n.options.infinite ? n.slideOffset += n.slideWidth * Math.floor(n.options.slidesToShow / 2) - n.slideWidth : !0 === n.options.centerMode && (n.slideOffset = 0, n.slideOffset += n.slideWidth * Math.floor(n.options.slidesToShow / 2)), e = !1 === n.options.vertical ? i * n.slideWidth * -1 + n.slideOffset : i * t * -1 + r, !0 === n.options.variableWidth && (o = n.slideCount <= n.options.slidesToShow || !1 === n.options.infinite ? n.$slideTrack.children(".slick-slide").eq(i) : n.$slideTrack.children(".slick-slide").eq(i + n.options.slidesToShow), e = !0 === n.options.rtl ? o[0] ? -1 * (n.$slideTrack.width() - o[0].offsetLeft - o.width()) : 0 : o[0] ? -1 * o[0].offsetLeft : 0, !0 === n.options.centerMode && (o = n.slideCount <= n.options.slidesToShow || !1 === n.options.infinite ? n.$slideTrack.children(".slick-slide").eq(i) : n.$slideTrack.children(".slick-slide").eq(i + n.options.slidesToShow + 1), e = !0 === n.options.rtl ? o[0] ? -1 * (n.$slideTrack.width() - o[0].offsetLeft - o.width()) : 0 : o[0] ? -1 * o[0].offsetLeft : 0, e += (n.$list.width() - o.outerWidth()) / 2)), e }, e.prototype.getOption = e.prototype.slickGetOption = function (i) { return this.options[i] }, e.prototype.getNavigableIndexes = function () { var i, e = this, t = 0, o = 0, s = []; for (!1 === e.options.infinite ? i = e.slideCount : (t = -1 * e.options.slidesToScroll, o = -1 * e.options.slidesToScroll, i = 2 * e.slideCount); t < i;)s.push(t), t = o + e.options.slidesToScroll, o += e.options.slidesToScroll <= e.options.slidesToShow ? e.options.slidesToScroll : e.options.slidesToShow; return s }, e.prototype.getSlick = function () { return this }, e.prototype.getSlideCount = function () { var e, t, o = this; return t = !0 === o.options.centerMode ? o.slideWidth * Math.floor(o.options.slidesToShow / 2) : 0, !0 === o.options.swipeToSlide ? (o.$slideTrack.find(".slick-slide").each(function (s, n) { if (n.offsetLeft - t + i(n).outerWidth() / 2 > -1 * o.swipeLeft) return e = n, !1 }), Math.abs(i(e).attr("data-slick-index") - o.currentSlide) || 1) : o.options.slidesToScroll }, e.prototype.goTo = e.prototype.slickGoTo = function (i, e) { this.changeSlide({ data: { message: "index", index: parseInt(i) } }, e) }, e.prototype.init = function (e) { var t = this; i(t.$slider).hasClass("slick-initialized") || (i(t.$slider).addClass("slick-initialized"), t.buildRows(), t.buildOut(), t.setProps(), t.startLoad(), t.loadSlider(), t.initializeEvents(), t.updateArrows(), t.updateDots(), t.checkResponsive(!0), t.focusHandler()), e && t.$slider.trigger("init", [t]), !0 === t.options.accessibility && t.initADA(), t.options.autoplay && (t.paused = !1, t.autoPlay()) }, e.prototype.initADA = function () { var e = this, t = Math.ceil(e.slideCount / e.options.slidesToShow), o = e.getNavigableIndexes().filter(function (i) { return i >= 0 && i < e.slideCount }); e.$slides.add(e.$slideTrack.find(".slick-cloned")).attr({ "aria-hidden": "true", tabindex: "-1" }).find("a, input, button, select").attr({ tabindex: "-1" }), null !== e.$dots && (e.$slides.not(e.$slideTrack.find(".slick-cloned")).each(function (t) { var s = o.indexOf(t); i(this).attr({ role: "tabpanel", id: "slick-slide" + e.instanceUid + t, tabindex: -1 }), -1 !== s && i(this).attr({ "aria-describedby": "slick-slide-control" + e.instanceUid + s }) }), e.$dots.attr("role", "tablist").find("li").each(function (s) { var n = o[s]; i(this).attr({ role: "presentation" }), i(this).find("button").first().attr({ role: "tab", id: "slick-slide-control" + e.instanceUid + s, "aria-controls": "slick-slide" + e.instanceUid + n, "aria-label": s + 1 + " of " + t, "aria-selected": null, tabindex: "-1" }) }).eq(e.currentSlide).find("button").attr({ "aria-selected": "true", tabindex: "0" }).end()); for (var s = e.currentSlide, n = s + e.options.slidesToShow; s < n; s++)e.$slides.eq(s).attr("tabindex", 0); e.activateADA() }, e.prototype.initArrowEvents = function () { var i = this; !0 === i.options.arrows && i.slideCount > i.options.slidesToShow && (i.$prevArrow.off("click.slick").on("click.slick", { message: "previous" }, i.changeSlide), i.$nextArrow.off("click.slick").on("click.slick", { message: "next" }, i.changeSlide), !0 === i.options.accessibility && (i.$prevArrow.on("keydown.slick", i.keyHandler), i.$nextArrow.on("keydown.slick", i.keyHandler))) }, e.prototype.initDotEvents = function () { var e = this; !0 === e.options.dots && (i("li", e.$dots).on("click.slick", { message: "index" }, e.changeSlide), !0 === e.options.accessibility && e.$dots.on("keydown.slick", e.keyHandler)), !0 === e.options.dots && !0 === e.options.pauseOnDotsHover && i("li", e.$dots).on("mouseenter.slick", i.proxy(e.interrupt, e, !0)).on("mouseleave.slick", i.proxy(e.interrupt, e, !1)) }, e.prototype.initSlideEvents = function () { var e = this; e.options.pauseOnHover && (e.$list.on("mouseenter.slick", i.proxy(e.interrupt, e, !0)), e.$list.on("mouseleave.slick", i.proxy(e.interrupt, e, !1))) }, e.prototype.initializeEvents = function () { var e = this; e.initArrowEvents(), e.initDotEvents(), e.initSlideEvents(), e.$list.on("touchstart.slick mousedown.slick", { action: "start" }, e.swipeHandler), e.$list.on("touchmove.slick mousemove.slick", { action: "move" }, e.swipeHandler), e.$list.on("touchend.slick mouseup.slick", { action: "end" }, e.swipeHandler), e.$list.on("touchcancel.slick mouseleave.slick", { action: "end" }, e.swipeHandler), e.$list.on("click.slick", e.clickHandler), i(document).on(e.visibilityChange, i.proxy(e.visibility, e)), !0 === e.options.accessibility && e.$list.on("keydown.slick", e.keyHandler), !0 === e.options.focusOnSelect && i(e.$slideTrack).children().on("click.slick", e.selectHandler), i(window).on("orientationchange.slick.slick-" + e.instanceUid, i.proxy(e.orientationChange, e)), i(window).on("resize.slick.slick-" + e.instanceUid, i.proxy(e.resize, e)), i("[draggable!=true]", e.$slideTrack).on("dragstart", e.preventDefault), i(window).on("load.slick.slick-" + e.instanceUid, e.setPosition), i(e.setPosition) }, e.prototype.initUI = function () { var i = this; !0 === i.options.arrows && i.slideCount > i.options.slidesToShow && (i.$prevArrow.show(), i.$nextArrow.show()), !0 === i.options.dots && i.slideCount > i.options.slidesToShow && i.$dots.show() }, e.prototype.keyHandler = function (i) { var e = this; i.target.tagName.match("TEXTAREA|INPUT|SELECT") || (37 === i.keyCode && !0 === e.options.accessibility ? e.changeSlide({ data: { message: !0 === e.options.rtl ? "next" : "previous" } }) : 39 === i.keyCode && !0 === e.options.accessibility && e.changeSlide({ data: { message: !0 === e.options.rtl ? "previous" : "next" } })) }, e.prototype.lazyLoad = function () { function e(e) { i("img[data-lazy]", e).each(function () { var e = i(this), t = i(this).attr("data-lazy"), o = i(this).attr("data-srcset"), s = i(this).attr("data-sizes") || n.$slider.attr("data-sizes"), r = document.createElement("img"); r.onload = function () { e.animate({ opacity: 0 }, 100, function () { o && (e.attr("srcset", o), s && e.attr("sizes", s)), e.attr("src", t).animate({ opacity: 1 }, 200, function () { e.removeAttr("data-lazy data-srcset data-sizes").removeClass("slick-loading") }), n.$slider.trigger("lazyLoaded", [n, e, t]) }) }, r.onerror = function () { e.removeAttr("data-lazy").removeClass("slick-loading").addClass("slick-lazyload-error"), n.$slider.trigger("lazyLoadError", [n, e, t]) }, r.src = t }) } var t, o, s, n = this; if (!0 === n.options.centerMode ? !0 === n.options.infinite ? s = (o = n.currentSlide + (n.options.slidesToShow / 2 + 1)) + n.options.slidesToShow + 2 : (o = Math.max(0, n.currentSlide - (n.options.slidesToShow / 2 + 1)), s = n.options.slidesToShow / 2 + 1 + 2 + n.currentSlide) : (o = n.options.infinite ? n.options.slidesToShow + n.currentSlide : n.currentSlide, s = Math.ceil(o + n.options.slidesToShow), !0 === n.options.fade && (o > 0 && o--, s <= n.slideCount && s++)), t = n.$slider.find(".slick-slide").slice(o, s), "anticipated" === n.options.lazyLoad) for (var r = o - 1, l = s, d = n.$slider.find(".slick-slide"), a = 0; a < n.options.slidesToScroll; a++)r < 0 && (r = n.slideCount - 1), t = (t = t.add(d.eq(r))).add(d.eq(l)), r--, l++; e(t), n.slideCount <= n.options.slidesToShow ? e(n.$slider.find(".slick-slide")) : n.currentSlide >= n.slideCount - n.options.slidesToShow ? e(n.$slider.find(".slick-cloned").slice(0, n.options.slidesToShow)) : 0 === n.currentSlide && e(n.$slider.find(".slick-cloned").slice(-1 * n.options.slidesToShow)) }, e.prototype.loadSlider = function () { var i = this; i.setPosition(), i.$slideTrack.css({ opacity: 1 }), i.$slider.removeClass("slick-loading"), i.initUI(), "progressive" === i.options.lazyLoad && i.progressiveLazyLoad() }, e.prototype.next = e.prototype.slickNext = function () { this.changeSlide({ data: { message: "next" } }) }, e.prototype.orientationChange = function () { var i = this; i.checkResponsive(), i.setPosition() }, e.prototype.pause = e.prototype.slickPause = function () { var i = this; i.autoPlayClear(), i.paused = !0 }, e.prototype.play = e.prototype.slickPlay = function () { var i = this; i.autoPlay(), i.options.autoplay = !0, i.paused = !1, i.focussed = !1, i.interrupted = !1 }, e.prototype.postSlide = function (e) { var t = this; t.unslicked || (t.$slider.trigger("afterChange", [t, e]), t.animating = !1, t.slideCount > t.options.slidesToShow && t.setPosition(), t.swipeLeft = null, t.options.autoplay && t.autoPlay(), !0 === t.options.accessibility && (t.initADA(), t.options.focusOnChange && i(t.$slides.get(t.currentSlide)).attr("tabindex", 0).focus())) }, e.prototype.prev = e.prototype.slickPrev = function () { this.changeSlide({ data: { message: "previous" } }) }, e.prototype.preventDefault = function (i) { i.preventDefault() }, e.prototype.progressiveLazyLoad = function (e) { e = e || 1; var t, o, s, n, r, l = this, d = i("img[data-lazy]", l.$slider); d.length ? (t = d.first(), o = t.attr("data-lazy"), s = t.attr("data-srcset"), n = t.attr("data-sizes") || l.$slider.attr("data-sizes"), (r = document.createElement("img")).onload = function () { s && (t.attr("srcset", s), n && t.attr("sizes", n)), t.attr("src", o).removeAttr("data-lazy data-srcset data-sizes").removeClass("slick-loading"), !0 === l.options.adaptiveHeight && l.setPosition(), l.$slider.trigger("lazyLoaded", [l, t, o]), l.progressiveLazyLoad() }, r.onerror = function () { e < 3 ? setTimeout(function () { l.progressiveLazyLoad(e + 1) }, 500) : (t.removeAttr("data-lazy").removeClass("slick-loading").addClass("slick-lazyload-error"), l.$slider.trigger("lazyLoadError", [l, t, o]), l.progressiveLazyLoad()) }, r.src = o) : l.$slider.trigger("allImagesLoaded", [l]) }, e.prototype.refresh = function (e) { var t, o, s = this; o = s.slideCount - s.options.slidesToShow, !s.options.infinite && s.currentSlide > o && (s.currentSlide = o), s.slideCount <= s.options.slidesToShow && (s.currentSlide = 0), t = s.currentSlide, s.destroy(!0), i.extend(s, s.initials, { currentSlide: t }), s.init(), e || s.changeSlide({ data: { message: "index", index: t } }, !1) }, e.prototype.registerBreakpoints = function () { var e, t, o, s = this, n = s.options.responsive || null; if ("array" === i.type(n) && n.length) { s.respondTo = s.options.respondTo || "window"; for (e in n) if (o = s.breakpoints.length - 1, n.hasOwnProperty(e)) { for (t = n[e].breakpoint; o >= 0;)s.breakpoints[o] && s.breakpoints[o] === t && s.breakpoints.splice(o, 1), o--; s.breakpoints.push(t), s.breakpointSettings[t] = n[e].settings } s.breakpoints.sort(function (i, e) { return s.options.mobileFirst ? i - e : e - i }) } }, e.prototype.reinit = function () { var e = this; e.$slides = e.$slideTrack.children(e.options.slide).addClass("slick-slide"), e.slideCount = e.$slides.length, e.currentSlide >= e.slideCount && 0 !== e.currentSlide && (e.currentSlide = e.currentSlide - e.options.slidesToScroll), e.slideCount <= e.options.slidesToShow && (e.currentSlide = 0), e.registerBreakpoints(), e.setProps(), e.setupInfinite(), e.buildArrows(), e.updateArrows(), e.initArrowEvents(), e.buildDots(), e.updateDots(), e.initDotEvents(), e.cleanUpSlideEvents(), e.initSlideEvents(), e.checkResponsive(!1, !0), !0 === e.options.focusOnSelect && i(e.$slideTrack).children().on("click.slick", e.selectHandler), e.setSlideClasses("number" == typeof e.currentSlide ? e.currentSlide : 0), e.setPosition(), e.focusHandler(), e.paused = !e.options.autoplay, e.autoPlay(), e.$slider.trigger("reInit", [e]) }, e.prototype.resize = function () { var e = this; i(window).width() !== e.windowWidth && (clearTimeout(e.windowDelay), e.windowDelay = window.setTimeout(function () { e.windowWidth = i(window).width(), e.checkResponsive(), e.unslicked || e.setPosition() }, 50)) }, e.prototype.removeSlide = e.prototype.slickRemove = function (i, e, t) { var o = this; if (i = "boolean" == typeof i ? !0 === (e = i) ? 0 : o.slideCount - 1 : !0 === e ? --i : i, o.slideCount < 1 || i < 0 || i > o.slideCount - 1) return !1; o.unload(), !0 === t ? o.$slideTrack.children().remove() : o.$slideTrack.children(this.options.slide).eq(i).remove(), o.$slides = o.$slideTrack.children(this.options.slide), o.$slideTrack.children(this.options.slide).detach(), o.$slideTrack.append(o.$slides), o.$slidesCache = o.$slides, o.reinit() }, e.prototype.setCSS = function (i) { var e, t, o = this, s = {}; !0 === o.options.rtl && (i = -i), e = "left" == o.positionProp ? Math.ceil(i) + "px" : "0px", t = "top" == o.positionProp ? Math.ceil(i) + "px" : "0px", s[o.positionProp] = i, !1 === o.transformsEnabled ? o.$slideTrack.css(s) : (s = {}, !1 === o.cssTransitions ? (s[o.animType] = "translate(" + e + ", " + t + ")", o.$slideTrack.css(s)) : (s[o.animType] = "translate3d(" + e + ", " + t + ", 0px)", o.$slideTrack.css(s))) }, e.prototype.setDimensions = function () { var i = this; !1 === i.options.vertical ? !0 === i.options.centerMode && i.$list.css({ padding: "0px " + i.options.centerPadding }) : (i.$list.height(i.$slides.first().outerHeight(!0) * i.options.slidesToShow), !0 === i.options.centerMode && i.$list.css({ padding: i.options.centerPadding + " 0px" })), i.listWidth = i.$list.width(), i.listHeight = i.$list.height(), !1 === i.options.vertical && !1 === i.options.variableWidth ? (i.slideWidth = Math.ceil(i.listWidth / i.options.slidesToShow), i.$slideTrack.width(Math.ceil(i.slideWidth * i.$slideTrack.children(".slick-slide").length))) : !0 === i.options.variableWidth ? i.$slideTrack.width(5e3 * i.slideCount) : (i.slideWidth = Math.ceil(i.listWidth), i.$slideTrack.height(Math.ceil(i.$slides.first().outerHeight(!0) * i.$slideTrack.children(".slick-slide").length))); var e = i.$slides.first().outerWidth(!0) - i.$slides.first().width(); !1 === i.options.variableWidth && i.$slideTrack.children(".slick-slide").width(i.slideWidth - e) }, e.prototype.setFade = function () { var e, t = this; t.$slides.each(function (o, s) { e = t.slideWidth * o * -1, !0 === t.options.rtl ? i(s).css({ position: "relative", right: e, top: 0, zIndex: t.options.zIndex - 2, opacity: 0 }) : i(s).css({ position: "relative", left: e, top: 0, zIndex: t.options.zIndex - 2, opacity: 0 }) }), t.$slides.eq(t.currentSlide).css({ zIndex: t.options.zIndex - 1, opacity: 1 }) }, e.prototype.setHeight = function () { var i = this; if (1 === i.options.slidesToShow && !0 === i.options.adaptiveHeight && !1 === i.options.vertical) { var e = i.$slides.eq(i.currentSlide).outerHeight(!0); i.$list.css("height", e) } }, e.prototype.setOption = e.prototype.slickSetOption = function () { var e, t, o, s, n, r = this, l = !1; if ("object" === i.type(arguments[0]) ? (o = arguments[0], l = arguments[1], n = "multiple") : "string" === i.type(arguments[0]) && (o = arguments[0], s = arguments[1], l = arguments[2], "responsive" === arguments[0] && "array" === i.type(arguments[1]) ? n = "responsive" : void 0 !== arguments[1] && (n = "single")), "single" === n) r.options[o] = s; else if ("multiple" === n) i.each(o, function (i, e) { r.options[i] = e }); else if ("responsive" === n) for (t in s) if ("array" !== i.type(r.options.responsive)) r.options.responsive = [s[t]]; else { for (e = r.options.responsive.length - 1; e >= 0;)r.options.responsive[e].breakpoint === s[t].breakpoint && r.options.responsive.splice(e, 1), e--; r.options.responsive.push(s[t]) } l && (r.unload(), r.reinit()) }, e.prototype.setPosition = function () { var i = this; i.setDimensions(), i.setHeight(), !1 === i.options.fade ? i.setCSS(i.getLeft(i.currentSlide)) : i.setFade(), i.$slider.trigger("setPosition", [i]) }, e.prototype.setProps = function () { var i = this, e = document.body.style; i.positionProp = !0 === i.options.vertical ? "top" : "left", "top" === i.positionProp ? i.$slider.addClass("slick-vertical") : i.$slider.removeClass("slick-vertical"), void 0 === e.WebkitTransition && void 0 === e.MozTransition && void 0 === e.msTransition || !0 === i.options.useCSS && (i.cssTransitions = !0), i.options.fade && ("number" == typeof i.options.zIndex ? i.options.zIndex < 3 && (i.options.zIndex = 3) : i.options.zIndex = i.defaults.zIndex), void 0 !== e.OTransform && (i.animType = "OTransform", i.transformType = "-o-transform", i.transitionType = "OTransition", void 0 === e.perspectiveProperty && void 0 === e.webkitPerspective && (i.animType = !1)), void 0 !== e.MozTransform && (i.animType = "MozTransform", i.transformType = "-moz-transform", i.transitionType = "MozTransition", void 0 === e.perspectiveProperty && void 0 === e.MozPerspective && (i.animType = !1)), void 0 !== e.webkitTransform && (i.animType = "webkitTransform", i.transformType = "-webkit-transform", i.transitionType = "webkitTransition", void 0 === e.perspectiveProperty && void 0 === e.webkitPerspective && (i.animType = !1)), void 0 !== e.msTransform && (i.animType = "msTransform", i.transformType = "-ms-transform", i.transitionType = "msTransition", void 0 === e.msTransform && (i.animType = !1)), void 0 !== e.transform && !1 !== i.animType && (i.animType = "transform", i.transformType = "transform", i.transitionType = "transition"), i.transformsEnabled = i.options.useTransform && null !== i.animType && !1 !== i.animType }, e.prototype.setSlideClasses = function (i) { var e, t, o, s, n = this; if (t = n.$slider.find(".slick-slide").removeClass("slick-active slick-center slick-current").attr("aria-hidden", "true"), n.$slides.eq(i).addClass("slick-current"), !0 === n.options.centerMode) { var r = n.options.slidesToShow % 2 == 0 ? 1 : 0; e = Math.floor(n.options.slidesToShow / 2), !0 === n.options.infinite && (i >= e && i <= n.slideCount - 1 - e ? n.$slides.slice(i - e + r, i + e + 1).addClass("slick-active").attr("aria-hidden", "false") : (o = n.options.slidesToShow + i, t.slice(o - e + 1 + r, o + e + 2).addClass("slick-active").attr("aria-hidden", "false")), 0 === i ? t.eq(t.length - 1 - n.options.slidesToShow).addClass("slick-center") : i === n.slideCount - 1 && t.eq(n.options.slidesToShow).addClass("slick-center")), n.$slides.eq(i).addClass("slick-center") } else i >= 0 && i <= n.slideCount - n.options.slidesToShow ? n.$slides.slice(i, i + n.options.slidesToShow).addClass("slick-active").attr("aria-hidden", "false") : t.length <= n.options.slidesToShow ? t.addClass("slick-active").attr("aria-hidden", "false") : (s = n.slideCount % n.options.slidesToShow, o = !0 === n.options.infinite ? n.options.slidesToShow + i : i, n.options.slidesToShow == n.options.slidesToScroll && n.slideCount - i < n.options.slidesToShow ? t.slice(o - (n.options.slidesToShow - s), o + s).addClass("slick-active").attr("aria-hidden", "false") : t.slice(o, o + n.options.slidesToShow).addClass("slick-active").attr("aria-hidden", "false")); "ondemand" !== n.options.lazyLoad && "anticipated" !== n.options.lazyLoad || n.lazyLoad() }, e.prototype.setupInfinite = function () { var e, t, o, s = this; if (!0 === s.options.fade && (s.options.centerMode = !1), !0 === s.options.infinite && !1 === s.options.fade && (t = null, s.slideCount > s.options.slidesToShow)) { for (o = !0 === s.options.centerMode ? s.options.slidesToShow + 1 : s.options.slidesToShow, e = s.slideCount; e > s.slideCount - o; e -= 1)t = e - 1, i(s.$slides[t]).clone(!0).attr("id", "").attr("data-slick-index", t - s.slideCount).prependTo(s.$slideTrack).addClass("slick-cloned"); for (e = 0; e < o + s.slideCount; e += 1)t = e, i(s.$slides[t]).clone(!0).attr("id", "").attr("data-slick-index", t + s.slideCount).appendTo(s.$slideTrack).addClass("slick-cloned"); s.$slideTrack.find(".slick-cloned").find("[id]").each(function () { i(this).attr("id", "") }) } }, e.prototype.interrupt = function (i) { var e = this; i || e.autoPlay(), e.interrupted = i }, e.prototype.selectHandler = function (e) { var t = this, o = i(e.target).is(".slick-slide") ? i(e.target) : i(e.target).parents(".slick-slide"), s = parseInt(o.attr("data-slick-index")); s || (s = 0), t.slideCount <= t.options.slidesToShow ? t.slideHandler(s, !1, !0) : t.slideHandler(s) }, e.prototype.slideHandler = function (i, e, t) { var o, s, n, r, l, d = null, a = this; if (e = e || !1, !(!0 === a.animating && !0 === a.options.waitForAnimate || !0 === a.options.fade && a.currentSlide === i)) if (!1 === e && a.asNavFor(i), o = i, d = a.getLeft(o), r = a.getLeft(a.currentSlide), a.currentLeft = null === a.swipeLeft ? r : a.swipeLeft, !1 === a.options.infinite && !1 === a.options.centerMode && (i < 0 || i > a.getDotCount() * a.options.slidesToScroll)) !1 === a.options.fade && (o = a.currentSlide, !0 !== t ? a.animateSlide(r, function () { a.postSlide(o) }) : a.postSlide(o)); else if (!1 === a.options.infinite && !0 === a.options.centerMode && (i < 0 || i > a.slideCount - a.options.slidesToScroll)) !1 === a.options.fade && (o = a.currentSlide, !0 !== t ? a.animateSlide(r, function () { a.postSlide(o) }) : a.postSlide(o)); else { if (a.options.autoplay && clearInterval(a.autoPlayTimer), s = o < 0 ? a.slideCount % a.options.slidesToScroll != 0 ? a.slideCount - a.slideCount % a.options.slidesToScroll : a.slideCount + o : o >= a.slideCount ? a.slideCount % a.options.slidesToScroll != 0 ? 0 : o - a.slideCount : o, a.animating = !0, a.$slider.trigger("beforeChange", [a, a.currentSlide, s]), n = a.currentSlide, a.currentSlide = s, a.setSlideClasses(a.currentSlide), a.options.asNavFor && (l = (l = a.getNavTarget()).slick("getSlick")).slideCount <= l.options.slidesToShow && l.setSlideClasses(a.currentSlide), a.updateDots(), a.updateArrows(), !0 === a.options.fade) return !0 !== t ? (a.fadeSlideOut(n), a.fadeSlide(s, function () { a.postSlide(s) })) : a.postSlide(s), void a.animateHeight(); !0 !== t ? a.animateSlide(d, function () { a.postSlide(s) }) : a.postSlide(s) } }, e.prototype.startLoad = function () { var i = this; !0 === i.options.arrows && i.slideCount > i.options.slidesToShow && (i.$prevArrow.hide(), i.$nextArrow.hide()), !0 === i.options.dots && i.slideCount > i.options.slidesToShow && i.$dots.hide(), i.$slider.addClass("slick-loading") }, e.prototype.swipeDirection = function () { var i, e, t, o, s = this; return i = s.touchObject.startX - s.touchObject.curX, e = s.touchObject.startY - s.touchObject.curY, t = Math.atan2(e, i), (o = Math.round(180 * t / Math.PI)) < 0 && (o = 360 - Math.abs(o)), o <= 45 && o >= 0 ? !1 === s.options.rtl ? "left" : "right" : o <= 360 && o >= 315 ? !1 === s.options.rtl ? "left" : "right" : o >= 135 && o <= 225 ? !1 === s.options.rtl ? "right" : "left" : !0 === s.options.verticalSwiping ? o >= 35 && o <= 135 ? "down" : "up" : "vertical" }, e.prototype.swipeEnd = function (i) { var e, t, o = this; if (o.dragging = !1, o.swiping = !1, o.scrolling) return o.scrolling = !1, !1; if (o.interrupted = !1, o.shouldClick = !(o.touchObject.swipeLength > 10), void 0 === o.touchObject.curX) return !1; if (!0 === o.touchObject.edgeHit && o.$slider.trigger("edge", [o, o.swipeDirection()]), o.touchObject.swipeLength >= o.touchObject.minSwipe) { switch (t = o.swipeDirection()) { case "left": case "down": e = o.options.swipeToSlide ? o.checkNavigable(o.currentSlide + o.getSlideCount()) : o.currentSlide + o.getSlideCount(), o.currentDirection = 0; break; case "right": case "up": e = o.options.swipeToSlide ? o.checkNavigable(o.currentSlide - o.getSlideCount()) : o.currentSlide - o.getSlideCount(), o.currentDirection = 1 }"vertical" != t && (o.slideHandler(e), o.touchObject = {}, o.$slider.trigger("swipe", [o, t])) } else o.touchObject.startX !== o.touchObject.curX && (o.slideHandler(o.currentSlide), o.touchObject = {}) }, e.prototype.swipeHandler = function (i) { var e = this; if (!(!1 === e.options.swipe || "ontouchend" in document && !1 === e.options.swipe || !1 === e.options.draggable && -1 !== i.type.indexOf("mouse"))) switch (e.touchObject.fingerCount = i.originalEvent && void 0 !== i.originalEvent.touches ? i.originalEvent.touches.length : 1, e.touchObject.minSwipe = e.listWidth / e.options.touchThreshold, !0 === e.options.verticalSwiping && (e.touchObject.minSwipe = e.listHeight / e.options.touchThreshold), i.data.action) { case "start": e.swipeStart(i); break; case "move": e.swipeMove(i); break; case "end": e.swipeEnd(i) } }, e.prototype.swipeMove = function (i) { var e, t, o, s, n, r, l = this; return n = void 0 !== i.originalEvent ? i.originalEvent.touches : null, !(!l.dragging || l.scrolling || n && 1 !== n.length) && (e = l.getLeft(l.currentSlide), l.touchObject.curX = void 0 !== n ? n[0].pageX : i.clientX, l.touchObject.curY = void 0 !== n ? n[0].pageY : i.clientY, l.touchObject.swipeLength = Math.round(Math.sqrt(Math.pow(l.touchObject.curX - l.touchObject.startX, 2))), r = Math.round(Math.sqrt(Math.pow(l.touchObject.curY - l.touchObject.startY, 2))), !l.options.verticalSwiping && !l.swiping && r > 4 ? (l.scrolling = !0, !1) : (!0 === l.options.verticalSwiping && (l.touchObject.swipeLength = r), t = l.swipeDirection(), void 0 !== i.originalEvent && l.touchObject.swipeLength > 4 && (l.swiping = !0, i.preventDefault()), s = (!1 === l.options.rtl ? 1 : -1) * (l.touchObject.curX > l.touchObject.startX ? 1 : -1), !0 === l.options.verticalSwiping && (s = l.touchObject.curY > l.touchObject.startY ? 1 : -1), o = l.touchObject.swipeLength, l.touchObject.edgeHit = !1, !1 === l.options.infinite && (0 === l.currentSlide && "right" === t || l.currentSlide >= l.getDotCount() && "left" === t) && (o = l.touchObject.swipeLength * l.options.edgeFriction, l.touchObject.edgeHit = !0), !1 === l.options.vertical ? l.swipeLeft = e + o * s : l.swipeLeft = e + o * (l.$list.height() / l.listWidth) * s, !0 === l.options.verticalSwiping && (l.swipeLeft = e + o * s), !0 !== l.options.fade && !1 !== l.options.touchMove && (!0 === l.animating ? (l.swipeLeft = null, !1) : void l.setCSS(l.swipeLeft)))) }, e.prototype.swipeStart = function (i) { var e, t = this; if (t.interrupted = !0, 1 !== t.touchObject.fingerCount || t.slideCount <= t.options.slidesToShow) return t.touchObject = {}, !1; void 0 !== i.originalEvent && void 0 !== i.originalEvent.touches && (e = i.originalEvent.touches[0]), t.touchObject.startX = t.touchObject.curX = void 0 !== e ? e.pageX : i.clientX, t.touchObject.startY = t.touchObject.curY = void 0 !== e ? e.pageY : i.clientY, t.dragging = !0 }, e.prototype.unfilterSlides = e.prototype.slickUnfilter = function () { var i = this; null !== i.$slidesCache && (i.unload(), i.$slideTrack.children(this.options.slide).detach(), i.$slidesCache.appendTo(i.$slideTrack), i.reinit()) }, e.prototype.unload = function () { var e = this; i(".slick-cloned", e.$slider).remove(), e.$dots && e.$dots.remove(), e.$prevArrow && e.htmlExpr.test(e.options.prevArrow) && e.$prevArrow.remove(), e.$nextArrow && e.htmlExpr.test(e.options.nextArrow) && e.$nextArrow.remove(), e.$slides.removeClass("slick-slide slick-active slick-visible slick-current").attr("aria-hidden", "true").css("width", "") }, e.prototype.unslick = function (i) { var e = this; e.$slider.trigger("unslick", [e, i]), e.destroy() }, e.prototype.updateArrows = function () { var i = this; Math.floor(i.options.slidesToShow / 2), !0 === i.options.arrows && i.slideCount > i.options.slidesToShow && !i.options.infinite && (i.$prevArrow.removeClass("slick-disabled").attr("aria-disabled", "false"), i.$nextArrow.removeClass("slick-disabled").attr("aria-disabled", "false"), 0 === i.currentSlide ? (i.$prevArrow.addClass("slick-disabled").attr("aria-disabled", "true"), i.$nextArrow.removeClass("slick-disabled").attr("aria-disabled", "false")) : i.currentSlide >= i.slideCount - i.options.slidesToShow && !1 === i.options.centerMode ? (i.$nextArrow.addClass("slick-disabled").attr("aria-disabled", "true"), i.$prevArrow.removeClass("slick-disabled").attr("aria-disabled", "false")) : i.currentSlide >= i.slideCount - 1 && !0 === i.options.centerMode && (i.$nextArrow.addClass("slick-disabled").attr("aria-disabled", "true"), i.$prevArrow.removeClass("slick-disabled").attr("aria-disabled", "false"))) }, e.prototype.updateDots = function () { var i = this; null !== i.$dots && (i.$dots.find("li").removeClass("slick-active").end(), i.$dots.find("li").eq(Math.floor(i.currentSlide / i.options.slidesToScroll)).addClass("slick-active")) }, e.prototype.visibility = function () { var i = this; i.options.autoplay && (document[i.hidden] ? i.interrupted = !0 : i.interrupted = !1) }, i.fn.slick = function () { var i, t, o = this, s = arguments[0], n = Array.prototype.slice.call(arguments, 1), r = o.length; for (i = 0; i < r; i++)if ("object" == typeof s || void 0 === s ? o[i].slick = new e(o[i], s) : t = o[i].slick[s].apply(o[i].slick, n), void 0 !== t) return t; return o } });
$(document).ready(function () {
    if ($('.product_description_read_more_text').length) {
        var product_description_height = $('.product_description_read_box').height();

        if (product_description_height < 160) {
            $('.product_description_read_more_text').addClass('hide');
        }

        $(document).on('click', '.product_description_read_more_text', function () {
            var $parents = $(this).parents(".product_description_read_more");
            if ($parents.hasClass('desc_open')) {
                $parents.removeClass('desc_open');
                $parents.find('.product_description_read_box').css('maxHeight', '160px');
                $(this).text("Lees meer");
            } else {
                $parents.addClass('desc_open');
                $parents.find('.product_description_read_box').css('maxHeight', 'unset');
                $(this).text("Lees minder");
            }
        });
    }

    $(document).ready(function () {
        $(".product-video-thumb-icon").click(function () {
            if (screen.width < 750) {
                var current_video_play = $(this).closest('.product-video').find("video.mob_video");
            } else {
                var current_video_play = $(this).closest('.product-video').find("video.desktop-only");
            }

            current_video_play[0].play();
            $(this).closest('.product-video').find(".product-video-thumb").hide();
        });

    });

    $(document).ready(function () {
        $(window).on("scroll", function () {
            $(".custom-bg-video video").each(function () {
                if (screen.width < 750) {
                    var $video = $('.product-video video.mob_video');
                } else {
                    var $video = $('.product-video video.desktop-only');
                }
                if ($video.length) {
                    var videoTop = $video.offset().top;
                    var videoBottom = videoTop + $video.outerHeight();
                    var windowTop = $(window).scrollTop();
                    var windowBottom = windowTop + $(window).height();

                    if (videoBottom > windowTop && videoTop < windowBottom) {
                        $(".product-video-thumb-icon").click();
                    } else {
                        // $video.click();
                    }
                }
            });
        });
    });


    $('.fragrance-note-slider').slick({
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 2,
        slidesToScroll: 1,
        cssEase: 'linear',
        responsive: [
            {
                breakpoint: 1026,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1
                }
            },
            {
                breakpoint: 800,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1
                }
            },
            {
                breakpoint: 480,
                settings: {
                    slidesToShow: 1.3,
                    infinite: false,
                    slidesToScroll: 1
                }
            }
            // You can unslick at a given breakpoint now by adding:
            // settings: "unslick"
            // instead of a settings object
        ]
    });

    if (window.matchMedia("(max-width: 768px)").matches) {
        $('.gallery-products-right').slick({
            dots: false,
            infinite: false,
            arrows: true,
            speed: 300,
            slidesToShow: 1.2,
            slidesToScroll: 1
        });
    }


    const observer = new MutationObserver(() => {
        const el = document.querySelector(".related-collection .product-list[slider='active']");
        if (el) {
            var $slickElements = $('.related-collection .product-list');

            $slickElements.slick({
                dots: false,
                arrows: false,
                infinite: true,
                slidesToShow: 4,
                swipeToSlide: true,
                slidesToScroll: 1,
                responsive: [
                    {
                        breakpoint: 1026,
                        settings: {
                            slidesToShow: 3,
                            slidesToScroll: 1
                        }
                    },
                    {
                        breakpoint: 800,
                        settings: {
                            slidesToShow: 2,
                            slidesToScroll: 1
                        }
                    },
                    {
                        breakpoint: 480,
                        settings: {
                            slidesToShow: 1.3,
                            infinite: false,
                            slidesToScroll: 1
                        }
                    }

                ]
            });

            observer.disconnect();
        }
    });

    observer.observe(document.body, { childList: true, subtree: true });




    $('.home_page_looking .collection-listing .product-grid--image-shape-square').slick({
        dots: false,
        infinite: true,
        slidesToShow: 5,
        slidesToScroll: 1,
        arrows: true,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1
                }
            },
            {
                breakpoint: 767,
                settings: {
                    slidesToShow: 1,
                    infinite: true,
                    slidesToScroll: 1
                }
            }
            
        ]
    });
});


// Quick-add / add-to-cart (16 okt 2026): lock per formulier i.p.v. globaal, foutafhandeling, loader alleen op de eigen kaart.
// Vangnet 17 okt 2026: Swiper (carrousel) annuleert een klik als de muis tijdens het klikken beweegt; zien we na een klik op de knop geen submit, dan versturen we zelf
document.addEventListener('click', function (e) {
    var hit = e.target && e.target.closest ? e.target.closest('form.product_add_cart_new') : null;
    if (!hit) return;
    var lbl = e.target.closest('label.quick-add-btn, .quick-add-submit, .addtocart');
    // Vangnet: een script (Swiper) kan de klik annuleren zodat het formulier niet verstuurt. Zien we 120 ms na een klik op de knop geen submit, dan versturen we zelf.
    if (!lbl || e.button !== 0) return;
    hit._qaClickAt = Date.now();
    setTimeout(function () {
        if (hit._qaSubmitAt && hit._qaSubmitAt >= hit._qaClickAt) return;
        if (hit.dataset.busy === '1') return;
        console.warn('[quick-add] klik gaf geen submit (geannuleerd door een ander script), alsnog versturen');
        if (typeof hit.requestSubmit === 'function') hit.requestSubmit(); else $(hit).trigger('submit');
    }, 120);
}, true);
$(document).on('submit', '.product_add_cart_new', function (e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    var form = e.currentTarget;
    form._qaSubmitAt = Date.now();
    // Per formulier: loopt er al een toevoeging, negeer de klik (geen globale 3 s-blokkade meer)
    if (form.dataset.busy === '1') {
        return;
    }
    handleAddToCart(e);
});

function quickAddSetBusy(form, busy) {
    if (!form) return;
    if (busy) {
        form.dataset.busy = '1';
        form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach(function (b) { b.disabled = true; });
        form.querySelectorAll('.loader').forEach(function (l) { l.classList.add('active'); });
    } else {
        delete form.dataset.busy;
        form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach(function (b) { b.disabled = false; });
        form.querySelectorAll('.loader').forEach(function (l) { l.classList.remove('active'); });
    }
}

function quickAddShowError(form, message) {
    // Eén vaste melding onderaan het scherm (een melding in de kaart zelf wordt door overflow afgesneden)
    var toast = document.getElementById('quick-add-toast');
    if (!toast) {
        toast = document.createElement('div');
        toast.id = 'quick-add-toast';
        toast.className = 'quick-add-toast';
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'assertive');
        document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(function () { toast.classList.remove('is-visible'); }, 4000);
}

function handleAddToCart(e) {
    var form = e.currentTarget;
    var item_id = parseInt($(form).find('[name="id"]').val());
    var item_id_id = form.getAttribute('id');
    var item_id_prop;
    if (!item_id) {
        quickAddShowError(form, 'Product niet gevonden');
        return;
    }
    quickAddSetBusy(form, true);
    if (document.querySelector("cart-drawer[open]")) {
        if (document.querySelector("cart-drawer[open]").querySelector(".free_product_container form[id='" + item_id_id + "']")) {
            item_id_prop = document.querySelector("cart-drawer[open]").querySelector(".free_product_container form[id='" + item_id_id + "']").getAttribute("gift-product");
        }
    } else {
        if (e.target.closest(".free_product_box_data")) {
            var giftForm = e.target.closest(".free_product_box_data").querySelector(".free_product_container form[id='" + item_id_id + "']");
            if (giftForm) item_id_prop = giftForm.getAttribute("gift-product");
        }
    }

    var delay = 0;
    if (item_id_prop === "true" && document.querySelector(".gift-product-item .cart-item__remove")) {
        document.querySelectorAll(".gift-product-item .cart-item__remove")
            .forEach(btn => btn.click());
        delay = 500;
    }

    let ajaxData = {
        id: item_id,
        quantity: 1
    };

    if (item_id_prop === "true") {
        ajaxData.properties = { "__Gift Product": "true" };
    }

    setTimeout(() => {
        // Vangnet 17 okt 2026: Shopify stuurt op elk cart-antwoord Clear-Site-Data (prefetchCache/prerenderCache);
        // Chrome houdt dat antwoord op desktop vast tot de prefetch-cache gewist is. Een GET van /cart.js krijgt die
        // header niet. Komt het antwoord van /cart/add.js niet binnen 1,5 s, dan kijken we zelf in de cart en ronden af
        // zodra het product erin staat. De add wordt NOOIT opnieuw verstuurd, er wordt alleen gekeken.
        var qa = { done: false, pollTimer: null, started: Date.now(), variantId: item_id };
        var baselinePromise = quickAddFetchCart().then(function (c) { return quickAddQtyOf(c, item_id); }).catch(function () { return null; });

        function finishSuccess(res, via) {
            if (qa.done) return;
            qa.done = true;
            clearTimeout(qa.pollTimer);
            if (window.console && via && via !== 'antwoord add.js') console.info('[quick-add] afgerond via ' + via + ' na ' + (Date.now() - qa.started) + ' ms');
            $.ajax({
                url: '/cart.js',
                type: 'GET',
                dataType: 'json',
                success: function (cartData) {
                    $(".cart-link__count").text(cartData.item_count);
                }
            });

            const cartForm = document.querySelector('cart-form');
            if (cartForm) cartForm.refresh();
            quickAddSetBusy(form, false);
            //cart notification on mobile
            var windowSize = window.innerWidth;
            if (windowSize < 750) {
                const notification = document.getElementById('AddedNotification').content.firstElementChild.cloneNode(true);
                notification.dataset.productImage = res.image;
                notification.dataset.productTitle = res.product_title;
                let notificationContainer = document.querySelector('.pageheader--sticky');
                if (!notificationContainer) {
                notificationContainer = document.querySelector('body');
                }
                notificationContainer.appendChild(notification);
            } else {
               const cart_drawer = document.querySelector(".js-cart-drawer");
                 if (cart_drawer && typeof cart_drawer.open === 'function') {
                   cart_drawer.open();
                   }
            }
        }

        function finishError(xhr, status) {
            if (qa.done) return;
            qa.done = true;
            clearTimeout(qa.pollTimer);
            quickAddSetBusy(form, false);
            var message = 'Toevoegen is niet gelukt, probeer opnieuw';
            if (status === 'timeout') {
                message = 'Het duurt te lang, probeer opnieuw';
            } else if (xhr && xhr.responseJSON && (xhr.responseJSON.description || xhr.responseJSON.message)) {
                message = xhr.responseJSON.description || xhr.responseJSON.message;
            } else if (xhr && xhr.status === 429) {
                message = 'Even geduld, probeer het zo opnieuw';
            }
            quickAddShowError(form, message);
            if (window.console) console.warn('[quick-add] /cart/add.js failed', xhr && xhr.status, status);
        }

        $.ajax({
            url: '/cart/add.js',
            type: 'POST',
            data: ajaxData,
            dataType: 'json',
            timeout: 30000,
            success: function (res) { finishSuccess(res, 'antwoord add.js'); },
            error: finishError
        });

        function poll() {
            if (qa.done) return;
            if (Date.now() - qa.started > 29000) return; // daarna rondt de timeout van de add het af
            quickAddFetchCart().then(function (c) {
                return baselinePromise.then(function (base) {
                    if (qa.done) return;
                    var now = quickAddQtyOf(c, item_id);
                    if (base !== null && now > base) {
                        var line = (c.items || []).filter(function (i) { return String(i.id) === String(item_id); })[0] || {};
                        finishSuccess({ image: line.image, product_title: line.product_title }, 'cart-check');
                    } else {
                        qa.pollTimer = setTimeout(poll, 700);
                    }
                });
            }).catch(function () {
                qa.pollTimer = setTimeout(poll, 1000);
            });
        }
        qa.pollTimer = setTimeout(poll, 1500);
    }, delay);
}

function quickAddFetchCart() {
    return fetch('/cart.js?qa=' + Date.now(), { cache: 'no-store', credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
    });
}

function quickAddQtyOf(cart, variantId) {
    return (cart && cart.items ? cart.items : []).reduce(function (sum, i) {
        return String(i.id) === String(variantId) ? sum + (i.quantity || 0) : sum;
    }, 0);
}
// Remove Free Gift from Cart if already added.
window.addEventListener('load', function () {
    const giftItems = document.querySelectorAll('.gift-product-item');

    if (giftItems.length > 1) {
        giftItems.forEach((item, index) => {
            if (index === 0) return;

            setTimeout(() => {
                const removeBtn = item.querySelector('.cart-item__remove');
                if (removeBtn) {
                    removeBtn.click();
                }
            }, 500 * (index - 1));
        });

    }
});
$(document).ready(function () {
    $('body').on('click', '.js-pair-form-submit', function (e) {
        e.preventDefault();
        $(this).closest('form').submit();
    })
})

$(document).ready(function () {
    if ($(window).width() < 768 && $('.icon-text-sec .icon-text-sec-container .icon-text-sec-block').length) {
        $('.icon-text-sec .icon-text-sec-container .icon-text-sec-block').slick({
            dots: true,
            infinite: true,
            arrows: false,
            slidesToShow: 2,
            slidesToScroll: 1,
            autoplay: true,
            autoplaySpeed: 2000,
        });
    }
});


if ($(".fragrance_read_more").length) {
    $(document).on('click', '.fragrance_read_more', function () {
        var $parents = $(this).parents(".fragrance-note-left");
        if ($parents.hasClass('desc_open')) {
            $parents.removeClass('desc_open');
            $parents.find('.content').css('maxHeight', '60px');
            $(this).text("Read More");
        } else {
            $parents.addClass('desc_open');
            $parents.find('.content').css('maxHeight', 'unset');
            $(this).text("Read Less");
        }
    });
}

// CD Sanket
$(document).ready(function () {
    window.HS_SLIDE_CART_LOADED = function (cart) {
        window.HS_SLIDE_CART_UPDATE();
    }
})

window.HS_SLIDE_CART_UPDATED = function (cart) {
    $('.free_product_box_data').removeClass('active');
    // $('.free_product_box_data').addClass('point_none');
    var free_html = $($('.free_product_cart').html());
    var all_pro_id = $('.collection_drop_pro_id').val().split('||');
    var index_slide = 0;


    if (cart.items.length > 0 && $('.hs-layout-content .cart_drawer_free_propduct').length < 1) {
        $('.hs-layout-content').append(free_html);
    }
    if (cart.items.length > 0 && $('.cart_page_free_product .cart_drawer_free_propduct').length < 1 && $('.cart_page_free_product').length) {
        $('.cart_page_free_product').append(free_html);
    }
    for (let i = 0; i < cart.items.length; i++) {
        var cart_data_id = cart.items[i].id;
        for (let j = 0; j < all_pro_id.length; j++) {
            if (cart_data_id == all_pro_id[j]) {
                if ($('.js_cart_item:not(.custom_free_product)').hasClass('reload_page')) {
                    location.reload();
                }
                $('.hs-product-' + cart_data_id + '').addClass('custom_free_product');
                $('.js_cart_item[pro_id="' + cart_data_id + '"]').addClass('custom_free_product');
                $('.main_cart_item[pro_id="' + cart_data_id + '"]').addClass('custom_free_product');
                $('.hs-product-' + cart_data_id + '').attr('pro_id', cart_data_id);
            }
        }
        $('.hs-product-' + cart_data_id + '').attr('product_ID', cart.items[i].product_id);

        $('.free_product_box_data').each(function () {
            if ($(this).attr('data_id') == cart_data_id) {
                index_slide += 1;
                $(this).addClass('active');
            }
        });

        if ($('.hs-sticky-cart .hs-item-cart-content.custom_free_product').length && $('.free_gift_html').length) {
            var free_gift_html = $('.free_gift_html').html();
            $('.hs-sticky-cart .hs-item-cart-content.custom_free_product').each(function () {
                if (!$(this).find('.hs-item-cart-description .free_gift_html_inner').length) {
                    $(this).find('.hs-item-cart-description .hs-item-top').after(free_gift_html);
                }
            });
        }

        // if($('.hs-sticky-cart .hs-item-cart-content').length && $('.custom_free_product').length){
        //   // 8854919872845
        //   var product_ID = parseInt($('.hs-sticky-cart .hs-item-cart-content').attr('product_ID'));
        //   if(product_ID == 8854919872845){
        //     console.log(window.HSSLIDECART.CART.total_price)
        //   }
        // }

        // if(cart.items.length == 1 && cart.items[i].price == 0){
        //   fetch('/cart/clear.js', {
        //     method: 'POST',
        //     headers: {
        //       'Content-Type': 'application/json',
        //       'Accept': 'application/json'
        //     }
        //   })
        //   .then(response => response.json())
        //   .then(data => {
        //     // console.log('Cart cleared:', data);
        //     window.HS_SLIDE_CART_UPDATE();
        //   })
        //   .catch((error) => {
        //     console.error('Error:', error);
        //   });
        // }

    }



    setTimeout(function () {
        $('.free_product_box_data').removeClass('point_none');
    }, 1500);

    if ($('.hs-sticky-cart .hs-item-cart-content').length) {
        // 8854919872845
        var product_ID = parseInt($('.hs-sticky-cart .hs-item-cart-content').attr('product_ID'));
        if (product_ID == 8854919872845) {
            if ($('.hs-site-cart-popup-layout .cart_drawer_free_propduct').length) {
                $('.hs-site-cart-popup-layout .cart_drawer_free_propduct').addClass('hidden');
            }
        } else {
            if ($('.hs-site-cart-popup-layout .cart_drawer_free_propduct').length) {
                $('.hs-site-cart-popup-layout .cart_drawer_free_propduct').removeClass('hidden');
            }
        }
    }
    enforceFreeProductQtyLimit();
}



$(document).on('click', '.free_product_arrow', '.free_product_arrow svg', function () {
    var pro_width = $('.free_product_box_data').outerWidth();
    if ($(this).hasClass('free_product_left_arrow')) {
        $('.free_product_container').animate({
            scrollLeft: "-=" + pro_width + ""
        }, "slow");
    } else {
        $('.free_product_container').animate({
            scrollLeft: "+=" + pro_width + ""
        }, "slow");
    }
})

// CLICK HANDLER
$(document).on('click', '.free_product_arrow', function () {
    var boxWidth = $('.free_product_box_data').outerWidth();
    var container = $('.free_product_container');
    var direction = $(this).hasClass('free_product_left_arrow') ? -1 : 1;

    container.animate(
        { scrollLeft: container.scrollLeft() + direction * boxWidth },
        300,
        function () {
            updateArrows();
        }
    );
});

// UPDATE ARROWS FUNCTION
function updateArrows() {
    var container = $('.free_product_container');

    var scrollLeft = container.scrollLeft();
    var maxScroll = container[0].scrollWidth - container.outerWidth();

    // LEFT ARROW DISABLE
    if (scrollLeft <= 0) {
        $('.free_product_left_arrow').addClass('disable-left');
    } else {
        $('.free_product_left_arrow').removeClass('disable-left');
    }

    // RIGHT ARROW DISABLE
    if (scrollLeft >= maxScroll - 2) {
        $('.free_product_right_arrow').addClass('disable-right');
    } else {
        $('.free_product_right_arrow').removeClass('disable-right');
    }
}

// CHECK ON LOAD + ON SCROLL
// $(document).ready(updateArrows);
$('.free_product_container').on('scroll', updateArrows);




$(window).scroll(function () {
    if ($(".collection_new_design .filter-container").length) {
        var coll_filter = $(".collection_new_design .filter-container").offset().top;
        var scrollTop = $(window).scrollTop();
        if (scrollTop >= coll_filter) {
            $(".collection_new_design .filter-container").addClass("scroll_avtice");
        } else {
            $(".collection_new_design .filter-container").removeClass("scroll_avtice")
        }
    }
})


if ($('.home_page_custom_img_text.custom-collection-shop .custom-collection-shop-block').length) {
    $('.home_page_custom_img_text.custom-collection-shop .custom-collection-shop-block').slick({
        infinite: false,
        slidesToShow: 4.1,
        slidesToScroll: 1,
        speed: 300,
        cssEase: 'ease-in-out',
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 3.1,
                    slidesToScroll: 1
                }
            },
            {
                breakpoint: 767,
                settings: {
                    slidesToShow: 1.1,
                    slidesToScroll: 1
                }
            }
        ]
    });
}


// $(document).on('click', function(event) {
//   // Your code here
//   console.log('Document was clicked', event);
// });

// document.addEventListener('contextmenu', function(e) {
//   e.preventDefault();
// });

function enforceFreeProductQtyLimit() {
    $('.custom_free_product').each(function () {
        var $item = $(this);
        var qty = parseInt($item.attr('data-item-qty'));
        var lineKey = $item.attr('data-item-key');

        if (qty > 1) {
            $.ajax({
                type: 'POST',
                url: '/cart/change.js',
                data: {
                    id: lineKey,
                    quantity: 1
                },
                dataType: 'json',
                success: function () {
                    window.HS_SLIDE_CART_UPDATE();
                }
            });
        }
    });
}

$(document).on("click", ".cart_drawer_free_product_head", function () {
    if ($('.cart_drawer_free_propduct').hasClass('active')) {
        $('.cart_drawer_free_propduct').removeClass('active');
        $('.free_product_container_main').slideUp();
    } else {
        $('.cart_drawer_free_propduct').addClass('active');
        $('.free_product_container_main').slideDown();
    }
});

if ($(window).width() > 768 && $('.scrolling-image-list .flexible-layout .slider .slider__grid').length) {
    $('.scrolling-image-list .flexible-layout .slider .slider__grid').slick({
        infinite: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        speed: 300,
        cssEase: 'ease-in-out',
        asNavFor: '.scrolling-image-list  .scrolling-image-list__content-list'
    });
}
if ($(window).width() > 768 && $('.scrolling-image-list  .scrolling-image-list__content-list').length) {
    $('.scrolling-image-list  .scrolling-image-list__content-list').slick({
        slidesToShow: 1,
        slidesToScroll: 1,
        asNavFor: '.scrolling-image-list .flexible-layout .slider .slider__grid',
        dots: false,
        infinite: false
        // arrows: false
    });
}

if ($('.how-it-works-container .how-it-works-steps').length) {
    $('.how-it-works-container .how-it-works-steps').slick({
        slidesToShow: 3,
        slidesToScroll: 1,
        dots: false,
        infinite: false,
        arrows: true,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1
                }
            }
        ]
    });
}

if (document.querySelector('.template-page.template-suffix-geurquiz iframe#ppf-iframe')) {
    const iframe = document.querySelector('.template-page.template-suffix-geurquiz iframe#ppf-iframe');
    iframe.onload = function () {
        const iframeDoc = iframe.contentWindow.document;

        const styleElement = iframeDoc.createElement('style');
        styleElement.type = 'text/css';

        styleElement.appendChild(iframeDoc.createTextNode(`
          body {
              height: auto !important;
          }
      `));

        iframeDoc.head.appendChild(styleElement);
    };
}


// #CD CODE...
$(document).on('click', '.upsell_btn .override_button', function (e) {
    e.preventDefault();
    if ($('.product-info .detail .upsell_pro .upsell_pro_list .pro_info:not(.default_pro) input:checked').length == 0) {
        $('.product-info .detail .upsell_pro .upsell_pro_list .pro_info:not(.default_pro)').css('border-color', 'red');
        $('.product-info .upsell_pro + .option_error').removeClass('hide')
    } else {
        $('.product-info .detail .upsell_pro .upsell_pro_list .pro_info:not(.default_pro)').css('border-color', '#c5c8d1');
        $('.product-info .upsell_pro + .option_error').addClass('hide')
    }

    var offset_top = $('.upsell_pro').offset().top - 200;
    window.scrollTo({ top: offset_top, behavior: 'smooth' });
});

$(document).on('change', '.product-info .detail .upsell_pro .upsell_pro_list .pro_info:not(.default_pro) input', function (e) {
    e.preventDefault();
    if ($('.product-info .detail .upsell_pro .upsell_pro_list .pro_info:not(.default_pro) input:checked').length == 1) {
        $('.product-info .detail .upsell_pro .upsell_pro_list .pro_info:not(.default_pro)').css('border-color', '#c5c8d1');
        $('.upsell_btn .override_button').addClass('hidden')
        $('.product-info .upsell_pro + .option_error').addClass('hide')
    }
});



(function () {
    const originalFetch = window.fetch;

    window.fetch = function (url, options) {
        const isCartAdd = url.includes('/cart/add.js'); // adding product
        const isCartChange = url.includes('/cart/change.js') || url.includes('/cart/update.js'); // qty update or removal

        return originalFetch.apply(this, arguments).then(response => {
            // Only run removal logic on change or update
            if (isCartChange) {
                response.clone().json().then(data => {
                    handleCartUpdate(data); // Run upsell logic only here
                });
            } else if (isCartAdd) {
                //console.log('Cart add detected - skipping upsell removal logic');
            }

            return response;
        });
    };
})();

function handleCartUpdate(cart) {
    if (!cart.items || !cart.items.length) return;

    cart.items.forEach(item => {
        // Check if this is an upsell product
        if (item.properties && item.properties._linked_main_product) {
            const linkedMainId = parseInt(item.properties._linked_main_product);

            // Find the linked main product
            const mainProduct = cart.items.find(main => main.id === linkedMainId);

            // === CASE 1: Main product completely removed -> remove upsell ===
            if (!mainProduct) {

                fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: item.key, quantity: 0 })
                })
                    .then(res => res.json())
                    .then(updatedCart => {
                        if (document.querySelector('cart-form')) {
                            setTimeout(function () {
                                document.querySelector('cart-form').refresh();
                            }, 1000);
                        }
                    })
                    .catch(error => console.error('Error removing upsell:', error));
            }

            // === CASE 2: Main product quantity decreased -> adjust upsell qty ===
            else if (item.quantity > mainProduct.quantity) {

                fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: item.key, quantity: mainProduct.quantity })
                })
                    .then(res => res.json())
                    .then(updatedCart => {
                       // console.log('Upsell qty adjusted:', updatedCart);
                        if (document.querySelector('cart-form')) {
                            setTimeout(function () {
                                document.querySelector('cart-form').refresh();
                            }, 1000);
                        }
                    });
            }
        }
    });
}

window.HS_SLIDE_CART_ADD_TO_CART = function ({ id, quantity }) {
    const mainProductInput = document.querySelector('product-form.product-info buy-buttons form input[name="id"]');
    const upsellInput = document.querySelector('.upsell_pro .upsell_pro_list .pro_info:not(.default_pro) input:checked');
    // const upsellqty = document.querySelector('.buy-buttons-row [name="quantity"]').value || 1;
    const upsellqty = document.querySelector('.buy-buttons-row [name="quantity"]').value;

    if (mainProductInput && upsellInput) {
        const current_pro_id = parseInt(mainProductInput.value);

        if (current_pro_id === id) {
            const current_upsell_id = parseInt(upsellInput.value);

            const upsellData = {
                items: [{
                    id: current_upsell_id,
                    quantity: upsellqty,
                    properties: {
                        _linked_main_product: current_pro_id
                    }
                }]
            };

            fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(upsellData)
            })
                .then(res => res.json())
                .then(response => {
                    console.log('Upsell added with link to main product:', response);
                    if (document.querySelector('cart-form')) {
                        setTimeout(function () {
                            document.querySelector('cart-form').refresh();
                        }, 1000);

                    }
                })
                .catch(error => console.error(error));
        }
    }
};



//CD auto redirect to the corresponding quiz page on language change.
document.addEventListener('DOMContentLoaded', function () {
    const lang = document.querySelector('.current_country')?.value;

    if (!lang) return;

    const quizSlugs = {
        'en': 'atelier-rebul-geurquizv2',
        'fr': 'atelier-rebul-geurquiz-fr',
        'nl': 'atelier-rebul-geurquiz-nl'
    };

    const basePath = '/tools/perfect-product-finder/';

    const expectedSlug = quizSlugs[lang];
    const expectedPath = `${lang === 'nl' ? '' : '/' + lang}${basePath}${expectedSlug}`;
    const expectedUrl = `${location.origin}${expectedPath}?instore=true#/`;

    const currentPath = window.location.pathname;

    const isOnQuizPage = Object.values(quizSlugs).some(slug => currentPath.includes(basePath + slug));

    if (isOnQuizPage && !currentPath.startsWith(expectedPath)) {
        window.location.href = expectedUrl;
    }
    const linksToGiftPage = document.querySelectorAll('a[href$="#giftPageLink"]');
    if (linksToGiftPage.length) {
        const giftPageLinks = {
            'en': '/tools/perfect-product-finder/atelier-rebul-geurquizv2',
            'fr': '/tools/perfect-product-finder/atelier-rebul-geurquiz-fr',
            'de': '/tools/perfect-product-finder/atelier-rebul-geurquiz-de',
            'nl': '/tools/perfect-product-finder/cadeau-quiz-nl'
        }
        const giftLink = giftPageLinks[lang];
        if (giftLink) {
            linksToGiftPage.forEach(link => link.href = `${location.origin}${giftLink}`);
        }
    }
});


window.addEventListener('load', function () {
    const giftItems = document.querySelectorAll('.gift-product-item');

    if (giftItems.length > 1) {
        giftItems.forEach((item, index) => {
            if (index === 0) return;

            setTimeout(() => {
                const removeBtn = item.querySelector('.cart-item__remove');
                if (removeBtn) {
                    removeBtn.click();
                }
            }, 500 * (index - 1));
        });

    }
});


let trendingSwiper;

document.addEventListener("initTrendingSlider", function () {

    const $slider = $('.trending-categories-products')[0];

    if (trendingSwiper) {
        trendingSwiper.destroy(true, true);
    }

    trendingSwiper = new Swiper($slider, {
        enabled: false,
        slidesPerView: "auto",
        loop: false,
        centeredSlides: false,
        spaceBetween: 0,
        navigation: {
        nextEl: '.custom-next',
        prevEl: '.custom-prev',
        },
        breakpoints: {
            768: {
              slidesPerView: 2,
              spaceBetween: 20,
              enabled: true
            }
        }
    });
    
});

document.dispatchEvent(new Event("initTrendingSlider"));

if ($('.product_custom_text_slide').length) {
    $('.product_custom_text_slide_box').slick({
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        fade: true,
        autoplay: true,
        autoplaySpeed: 3000
    });
}



$('body').on('click', '.cursorPointer', function(){
  $(this).find('.loader').addClass('active');
});

//CD auto redirect to the corresponding quiz page on language change.
document.addEventListener('DOMContentLoaded', function () {
    const lang = document.querySelector('.current_country')?.value;
    if (!lang) return;

    const isDotDe = location.hostname.includes('atelierrebul.de');

    const scentQuizSlugs = {
        'en': 'atelier-rebul-geurquizv2',
        'fr': 'atelier-rebul-geurquiz-fr',
        'nl': 'atelier-rebul-geurquiz-nl',
        'de': 'atelier-rebul-geurquiz-de'
    };

    const giftQuizSlugs = {
        'en': 'cadeau-quiz-en',
        'fr': 'cadeau-quiz-fr',
        'nl': 'cadeau-quiz-nl',
        'de': 'cadeau-quiz-de'
    };

    const basePath = '/tools/perfect-product-finder/';
    const skipLangPrefix = lang === 'nl' || (lang === 'de' && isDotDe);

    const expectedScentQSlug = scentQuizSlugs[lang];
    const expectedScentQPath = `${skipLangPrefix ? '' : '/' + lang}${basePath}${expectedScentQSlug}`;
    const expectedScentQUrl = `${location.origin}${expectedScentQPath}?instore=true#/`;

    const expectedGiftQSlug = giftQuizSlugs[lang];
    const expectedGiftQPath = `${skipLangPrefix ? '' : '/' + lang}${basePath}${expectedGiftQSlug}`;
    const expectedGiftQUrl = `${location.origin}${expectedGiftQPath}?instore=true#/`;

    const currentPath = window.location.pathname;

    const isOnScentQuizPage = Object.values(scentQuizSlugs).some(slug => currentPath.includes(basePath + slug));
    const isOnGiftQuizPage = Object.values(giftQuizSlugs).some(slug => currentPath.includes(basePath + slug));

    if (isOnScentQuizPage && !currentPath.startsWith(expectedScentQPath)) {
        window.location.href = expectedScentQUrl;
    }
    if (isOnGiftQuizPage && !currentPath.startsWith(expectedGiftQPath)) {
        window.location.href = expectedGiftQUrl;
    }

    document.querySelectorAll('a[href$="#scentQuiz"]').forEach(link => link.href = expectedScentQUrl);
    document.querySelectorAll('a[href$="#giftFinder"]').forEach(link => link.href = expectedGiftQUrl);
});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(link => {
    const href = link.getAttribute('href');

    if (href && !href.endsWith('/')) {
      link.setAttribute('href', href + '/');
    }
  });
});